import {Component, EventEmitter, Inject, Input, OnChanges, OnInit, Output} from '@angular/core';
import {DomainService} from '../../services/domain.service';
import {Title} from '@angular/platform-browser';
import {CreateRequestHelperObject} from '../../model/CreateRequestHelperObject';
import {Location} from '@angular/common';
import {ServerConstants} from '../../model/ServerConstants';
import {Router} from '@angular/router';
import {DomainUtilService} from '../../util/domain-util.service';
import {CountriesListService} from '../../services/countries-list.service';
import {TopLevelDomain} from '@iana-rzms/bff-sdk';
import {ChangeVO} from '../../generate/ChangeVO';
import {RequestMetadata} from '../../generate/RequestMetadata';
import {ChangeRequestVO} from '../../generate/TldChangeResultVO';
import {CommonStrings} from '../../model/CommonStrings';
import {ComponentDeactivateGuardService} from '../../services/component-deactivate-guard.service';
import {AuthService} from "../../services/auth.service";
import {PolicyType} from '../../model/PolicyType';

@Component({
    selector: 'domain-detail',
    templateUrl: './domain-detail.component.html',
    styleUrls: ['./domain-detail.component.scss'],
    standalone: false
})
export class DomainDetailComponent implements OnInit, OnChanges {

  @Input()
  domain: TopLevelDomain;

  @Input()
  name: string;

  @Input()
  viewMode: boolean;

  domainChange: ChangeVO;

  @Output()
  helperOutput = new EventEmitter<CreateRequestHelperObject>();

  domainModified: boolean;

  nsChange: ChangeVO;
  registryURLChange: ChangeVO;
  whoIsChange: ChangeVO;
  trustAnchorsChange: ChangeVO;
  organizationChange: ChangeVO;
  adminContactChange: ChangeVO;
  techContactChange: ChangeVO;
  rdapServerChange: ChangeVO;
  authPolicyChange: ChangeVO;
  metaDataChange: ChangeVO;
  authPolicyUsersChange: ChangeVO;

  @Input()
  requestMetadata: RequestMetadata;

  @Input()
  updateChangeRequest: boolean;

  allChanges = new Map<string, Array<string>>();
  diffFieldNames: Array<string> = [];
  loadingData: boolean;
  serverConstants = ServerConstants;
  editChangeRequestMode: boolean;
  screen: number;
  acEditMode: boolean;
  tcEditMode: boolean;
  openCangeRequestsWithSummaries: ChangeRequestVO[] = [];
  hasTechnicalPermission: boolean = false;
  hasNonTechnicalPermission: boolean = false;

  constructor(private domainService: DomainService,
              private domainUtil: DomainUtilService,
              @Inject('env') private environment,
              private authService: AuthService,
              private titleService: Title, private location: Location, private router: Router, private countriesListService: CountriesListService, private canDeactivate: ComponentDeactivateGuardService) {

  }


  ngOnInit() {
    if (this.domain == null) {
      this.router.navigateByUrl('/user/home');
    }
    this.hasTechnicalPermission = this.userHasPolicy(PolicyType.TECHNICAL);
    this.hasNonTechnicalPermission = this.userHasPolicy(PolicyType.NON_TECHNICAL);
    this.screen = 0;
    this.titleService.setTitle('Review Domain Details');
    this.getOpenChangeRequests();
    if (this.domain != null && !this.viewMode) {
      this.buildDiffNames();
      this.modifiedDomain(this.domain);
    }

    if (this.updateChangeRequest) {
      this.editChangeRequestMode = true;
    }

    this.countriesListService.load();

  }

  private userHasPolicy(policyType: PolicyType): boolean {
    if (this.environment.config.interfaceType === 'STAFF') {
      return true;
    }
    const currentUser = this.authService.currentUserAuthVO();
    for (let policyUser of this.domain.authPolicy.users) {
      if (policyUser.id == currentUser.id) {
        for (let permission of policyUser.permissions) {
          if (permission.policyType == policyType && (permission.permission == 'Approve' || permission.permission == 'Submit')) {
            return true;
          }
        }
      }
    }
    return false;
  }

  private getOpenChangeRequests() {
    this.domainService.getDomainOpenChangeRequests(this.domain.id).subscribe(data => {
      this.openCangeRequestsWithSummaries = data;
    }, error => {

    });
  }

  modifiedDomain(modifiedTLD: TopLevelDomain) {
    this.isDomainModified(modifiedTLD);
  }

  isDomainModified(domain: TopLevelDomain) {
    this.loadingData = true;
    if (domain != null) {
      const previousLocks = domain.locks ?? this.domain?.locks;
      let diffData: any;
      this.domainService.isDomainModified(domain).subscribe(data => {
        diffData = data.changes;
        this.domain = { ...data.futureTld, locks: previousLocks ?? data.futureTld.locks };
      }, () => {
        console.log('Unable to retrive diff information');
        this.loadingData = false;
      }, () => {
        this.applyCSSChangesIfApplicable(diffData);
        this.loadingData = false;
      });
    }
  }

  finalizeRequest(val: string) {
    this.canDeactivate.formEditEnded();
    if (val === CommonStrings.CONFIRM) {
      this.titleService.setTitle('Review Changes');
      const helper: CreateRequestHelperObject = {
        domainChange: this.domainChange,
        domain: this.domain,
        allChanges: this.allChanges
      };
      this.helperOutput.emit(helper);
    } else if (val === CommonStrings.CANCEL) {
      this.location.back();
    }
  }

  viewPendingChanges() {

  }

  private applyCSSChangesIfApplicable(changevo: ChangeVO) {
    this.domainModified = false;
    this.initChanges();

    this.domainChange = changevo;
    for (const change of changevo.changes) {
      if (this.isShowFinalizeRequest(change)) {
        this.domainModified = true;
        this.canDeactivate.formEditInitiated();
      }
      this.applyCssChange(change);
    }
  }

  initChanges() {
    this.nsChange = null;
    this.registryURLChange = null;
    this.whoIsChange = null;
    this.trustAnchorsChange = null;
    this.organizationChange = null;
    this.adminContactChange = null;
    this.techContactChange = null;
    this.rdapServerChange = null;
    this.authPolicyChange = null;
    this.metaDataChange = null;

  }

  applyCssChange(change: ChangeVO) {
    if (change.field === ServerConstants.ALL_NAME_SERVERS_FIELD_NAME) {
      this.nsChange = change;
    } else if (change.field === ServerConstants.REGISTRYURL_FIELD_NAME) {
      this.registryURLChange = change;
    } else if (change.field === ServerConstants.WHOIS_FIELD_NAME) {
      this.whoIsChange = change;
    } else if (change.field === ServerConstants.ALL_TRUST_ANCHORS_FIELD_NAME) {
      this.trustAnchorsChange = change;
    } else if (change.field === ServerConstants.SPONSORING_FIELD_NAME) {
      this.organizationChange = change;
    } else if (change.field === ServerConstants.ADMINISTRATIVE_FIELD_NAME) {
      this.adminContactChange = change;
    } else if (change.field === ServerConstants.TECHNICAL_FIELD_NAME) {
      this.techContactChange = change;
    } else if (change.field === ServerConstants.ALL_RDAP_SERVERS_FIELD_NAME) {
      this.rdapServerChange = change;
    } else if (change.field === ServerConstants.AUTH_POLICY_FIELD_NAME) {
      this.authPolicyChange = change;
    } else if (change.field === ServerConstants.AUTH_POLICY_USERS_FIELD_NAME) {
      this.authPolicyUsersChange = change;
    } else if (change.field === ServerConstants.META_DATA_FIELD_NAME) {
      this.metaDataChange = change;
    }

  }


  private isShowFinalizeRequest(change: ChangeVO): boolean {
    return this.diffFieldNames.indexOf(change.field) > -1;
  }

  private buildDiffNames() {
    this.diffFieldNames.push(ServerConstants.NAME_SERVERS_FIELD_NAME);
    this.diffFieldNames.push(ServerConstants.REGISTRYURL_FIELD_NAME);
    this.diffFieldNames.push(ServerConstants.WHOIS_FIELD_NAME);
    this.diffFieldNames.push(ServerConstants.TRUST_ANCHORS_FIELD_NAME);
    this.diffFieldNames.push(ServerConstants.SPONSORING_FIELD_NAME);
    this.diffFieldNames.push(ServerConstants.ADMINISTRATIVE_FIELD_NAME);
    this.diffFieldNames.push(ServerConstants.TECHNICAL_FIELD_NAME);
    this.diffFieldNames.push(ServerConstants.RDAP_SERVER_FIELD_NAME);
    this.diffFieldNames.push(ServerConstants.AUTH_POLICY_FIELD_NAME);
    this.diffFieldNames.push(ServerConstants.META_DATA_FIELD_NAME);

  }

  isRoot(): boolean {
    return this.domain.name.trim() === '.';
  }

  ngOnChanges(changes: any): void {

  }

  switchView(context: string) {
    if (context === 'ZONE') {
      this.screen = 0;
    } else {
      this.screen = 1;
    }
  }

  acEditModeEnabled($event: boolean) {
    this.acEditMode = $event;
  }

  tcEditModeEnabled($event: boolean) {
    this.tcEditMode = $event;
  }

  getSize(type: string) {
    let editMode: boolean;
    let margin_View_css: string;
    let margin_edit_css: string;
    if (type === 'AC') {
      editMode = this.acEditMode;
      margin_View_css = 'margin-right';
      margin_edit_css = 'margin-bottom';
    } else {
      editMode = this.tcEditMode;
      margin_View_css = 'margin-left';
      margin_edit_css = 'margin-top';
    }

    if (editMode) {
      return 'cell large-12 small-12 medium-12 ' + margin_edit_css;
    } else {
      return 'cell large-4 small-12 medium-12 ' + margin_View_css;
    }
  }

}
